import { Typography } from "@suraasa/placebo-ui"
import { Activity } from "api/resources/analytics/types"
import clsx from "clsx"

const CircleProgress = ({
  value,
  total,
  color,
  radius,
  isCourse,
}: {
  value: number
  total: number
  color: string
  radius: number
  isCourse?: boolean
}) => {
  const circumference = 2 * Math.PI * radius
  const MIN_PROGRESS = 0.001

  let percentage: number

  if (total === 0 && value === 0) {
    percentage = MIN_PROGRESS
  } else if (total > 0) {
    if (value === 0) {
      percentage = MIN_PROGRESS
    } else {
      percentage = value / total
      if (percentage > 1) percentage = 1
    }
  } else {
    percentage = MIN_PROGRESS
  }

  const offset = circumference * (1 - percentage)

  return (
    <svg
      className={clsx("absolute left-0 top-0 size-20 -rotate-90", {
        "sm:size-[200px]": isCourse,
      })}
      viewBox="0 0 100 100"
    >
      <circle
        cx="50"
        cy="50"
        r={radius}
        stroke="#E5E7EB"
        strokeWidth="3"
        fill="none"
      />
      <circle
        cx="50"
        cy="50"
        r={radius}
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 1s ease-out" }}
      />
    </svg>
  )
}

interface ActivityBreakdownCardProps {
  isCourse?: boolean
  activities: Activity[]
}

const ActivityBreakdownCard = ({
  isCourse,
  activities,
}: ActivityBreakdownCardProps) => {
  const radii = [38, 32, 26, 20]

  return (
    <div className="w-full py-2.5 pr-2.5">
      <Typography className="mb-1 pl-2.5 text-strongSmallBody text-muted">
        Activity Breakdown
      </Typography>
      <div
        className={clsx("flex gap-1", {
          "flex-col sm:flex-row sm:items-center sm:gap-3": isCourse,
          "flex-row items-center gap-3 md:flex-col md:items-start md:gap-1":
            !isCourse,
          "sm:flex-row-reverse !justify-between": isCourse,
        })}
      >
        <div
          className={clsx("relative ml-0.5 size-20", {
            "sm:size-[200px]": isCourse,
          })}
        >
          {activities.map((activity, index) => (
            <CircleProgress
              key={activity.label}
              value={activity.value}
              total={activity.total}
              color={activity.color}
              radius={radii[index]}
              isCourse={isCourse}
            />
          ))}
        </div>
        <div
          className={clsx("flex shrink-0 flex-col gap-1", {
            "px-2.5 sm:p-0": isCourse,
            "md:px-2.5": !isCourse,
            "p-2.5 !ml-2.5": isCourse,
          })}
        >
          {activities.map(activity => (
            <div key={activity.label} className="flex items-center gap-2">
              <span
                className="size-1.75 shrink-0 rounded-full"
                style={{ backgroundColor: activity.color }}
              ></span>
              <Typography className="text-smallBody text-muted">
                {activity.label}{" "}
                <span className="font-bold text-onSurface-900">
                  {activity.value}
                </span>{" "}
                / {activity.total}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ActivityBreakdownCard
